/**
 * A cell's evaluation state (docs/SHEETS.md 3, CLAUDE.md 14.2).
 *
 * Never colour alone. A healthy cell gets an icon only -- a word beside every one of
 * two hundred rows is noise -- but anything that is not healthy carries an icon and
 * a label, because that is the state a reader must not miss, and because greyscale
 * printing and colour-vision deficiency both flatten the colour away.
 */
import { AlertTriangle, Check, CircleDashed, CircleSlash, Clock, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
import type { CellStatus, EvalErrorKind } from '../model/types'

const STATUS = {
  ok: { icon: Check, className: 'text-[var(--status-pass)]', key: 'sheets.cell.ok', silent: true },
  evaluating: {
    icon: CircleDashed,
    className: 'text-accent',
    key: 'sheets.cell.evaluating',
    silent: false,
  },
  stale: {
    icon: Clock,
    className: 'text-[var(--status-na)]',
    key: 'sheets.cell.stale',
    silent: false,
  },
  unevaluated: {
    icon: CircleSlash,
    className: 'text-[var(--status-na)]',
    key: 'sheets.cell.unevaluated',
    silent: true,
  },
  error: {
    icon: AlertTriangle,
    className: 'text-[var(--status-warn)]',
    key: 'sheets.cell.error',
    silent: false,
  },
} as const satisfies Record<
  CellStatus,
  { icon: typeof Check; className: string; key: string; silent: boolean }
>

/** An undefined symbol is a harder failure than a unit slip, and reads as one. */
const ERROR_LABEL: Partial<Record<EvalErrorKind, string>> = {
  units: 'sheets.cell.units_label',
  cycle: 'sheets.error.title.cycle',
  undefined: 'sheets.error.title.undefined',
  syntax: 'sheets.error.title.syntax',
  timeout: 'sheets.error.title.timeout',
  limit: 'sheets.error.title.limit',
}

export function CellStatusBadge({
  status,
  errorKind,
  className,
}: {
  status: CellStatus
  errorKind?: EvalErrorKind
  className?: string
}) {
  const { t } = useTranslation('sheets')
  const style = STATUS[status]
  const Icon = status === 'error' && errorKind === 'undefined' ? XCircle : style.icon
  const labelKey = (status === 'error' && errorKind && ERROR_LABEL[errorKind]) || style.key
  const label = t(labelKey)

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 text-xs',
        style.className,
        className,
      )}
      // The label is always in the accessibility tree even when it is not painted,
      // so a screen reader never has to infer a state from an icon.
      aria-label={label}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {!style.silent && <span className="font-medium">{label}</span>}
    </span>
  )
}
